import { DateTime } from 'luxon'
import ButtonComponent from '../../../../../../components/stories/atoms/button'
import {
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../../../../components/stories/atoms/button/constants'
import {
  Info,
  Settings
} from '../../../../../../components/stories/atoms/icons/core'
import { Project } from '../../../../../../components/stories/atoms/icons/entities'
import { iconSizeOptions } from '../../../../../../components/stories/atoms/icons/icons-sizes'
import SkeletonComponent from '../../../../../../components/stories/atoms/skeleton'
import BadgesComponent from '../../../../../../components/stories/atoms/badges'
import {
  badgeColorOptions,
  badgeSizeOptions,
  badgesVariantOptions
} from '../../../../../../components/stories/atoms/badges/constants'
import colorsOptions from '../../../../../../components/stories/fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions
} from '../../../../../../components/stories/fundamentals/text-styles/constants'
import { TableRowText } from '../../../../../../components/stories/organisms/table/components/row/components/table-row-text'
import {
  RowText,
  RowTextContainer
} from '../../../../../../components/stories/organisms/table/components/row/styled-components'
import TableRowCell from '../../../../../../components/stories/organisms/table/components/row/table-row-cell'
import {
  tableColumnSizeOptions,
  tableHeaderOrientationOptions,
  tableHeaderTypeOptions
} from '../../../../../../components/stories/organisms/table/constants'
import { projectTreeItemTypeOptions } from '../../../../../../react-query/project/query/use-get-project-tree-query'
import { myProjectSortOptions } from '../../../../../../react-query/project/query/use-get-my-projects-query'
import { handleRoleColor } from '../../../../../../components/copy-project-modal/constants'
import MyProjectsTableContextMenu from '../../../my-projects-table-context-menu'
import { ROLES } from '../../../../../TeamsManagement/constants'
import TooltipComponent from '../../../../../../components/stories/atoms/tooltip'
import {
  tooltipAlignOptions,
  tooltipArrowAlignmentOptions,
  tooltipSideOptions,
  tooltipSizeOptions
} from '../../../../../../components/stories/atoms/tooltip/constants'

const projectMenuTableSkeletonRowCount = 8

const IconTooltip = ({ description, children }) => (
  <TooltipComponent.Provider skipDelayDuration={0} delayDuration={0}>
    <TooltipComponent.Root>
      <TooltipComponent.Trigger>{children}</TooltipComponent.Trigger>
      <TooltipComponent.Content
        side={tooltipSideOptions.LEFT}
        align={tooltipAlignOptions.CENTER}
        size={tooltipSizeOptions.MEDIUM}
      >
        {description}
        <TooltipComponent.Arrow
          arrowAlignment={tooltipArrowAlignmentOptions.CENTER}
        />
      </TooltipComponent.Content>
    </TooltipComponent.Root>
  </TooltipComponent.Provider>
)

const projectMenuFormatMessageFallback = ({ id, defaultMessage }) =>
  defaultMessage ?? id

const formatRelativeDate = (value) => {
  if (!value) return ''

  const parsedDate = DateTime.fromISO(value)

  if (!parsedDate.isValid) return value

  return parsedDate.toRelative()
}

const renderTableCellSkeleton = () => (
  <SkeletonComponent
    isLoading
    skeletonShape={{
      width: '100%',
      height: '16px',
      borderRadius: '6px'
    }}
  />
)

const renderTitleCellSkeleton = () => (
  <TableRowCell orientation={tableHeaderOrientationOptions.LEFT}>
    {renderTableCellSkeleton()}
  </TableRowCell>
)

const renderTextCell = ({ isActive, value }) => (
  <TableRowCell>
    <TableRowText
      mainText={
        <RowText
          weight={textStyleWeightOptions['TEXT-500']}
          size={textStyleSizeOptions['TEXT-S']}
          color={
            isActive
              ? colorsOptions.greys['grey-90']
              : colorsOptions.greys['grey-40']
          }
        >
          {value}
        </RowText>
      }
    />
  </TableRowCell>
)

export const buildDataSkeleton = (
  rowCount = projectMenuTableSkeletonRowCount
) =>
  Array.from({ length: rowCount }, (_, index) => ({
    id: `my-projects-table-skeleton-${index}`,
    name: '',
    type: projectTreeItemTypeOptions.PROJECT,
    active: true,
    organisationName: '',
    role: null,
    lastModified: null,
    lastModifiedLabel: '',
    openPins: 0,
    openTasks: 0,
    canCopyProject: false,
    isSkeleton: true
  }))

export const buildData = (
  data = [],
  {
    canCreateProjects = false,
    currentOrganizationId = null,
    hasPaidLicense = false
  } = {}
) => {
  if (!Array.isArray(data)) return []

  return data.map((item) => ({
    id: item.id,
    name: item.name,
    type: item.type ?? projectTreeItemTypeOptions.PROJECT,
    active: item.active ?? true,
    parentId: item.parentId ?? null,
    organisationId: item.organisationId ?? null,
    organisationName: item.organisationName ?? '',
    role: item.role ?? null,
    lastModified: item.lastModified ?? null,
    lastModifiedLabel: formatRelativeDate(item.lastModified),
    openPins: item.openPins ?? item.openPinCount ?? 0,
    openTasks: item.openTasks ?? item.openTaskCount ?? 0,
    canCopyProject:
      Boolean(item.active ?? true) &&
      hasPaidLicense &&
      item.organisationId === currentOrganizationId &&
      canCreateProjects
  }))
}

export const buildColumnsDefault = ({
  formatMessage = projectMenuFormatMessageFallback,
  onCopyProject = null,
  onLeaveProject = null,
  onOpenProject = () => {},
  onOpenProjectPanel = () => {},
  onOpenProjectSettings = () => {},
  isDesktop
} = {}) => [
  {
    id: myProjectSortOptions.NAME,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    cell: ({ row }) => {
      const isActive = row.original.active
      const inactiveLabel = formatMessage({
        id: 'inactive',
        defaultMessage: 'Inactive'
      })

      return (
        <TableRowCell orientation={tableHeaderOrientationOptions.LEFT}>
          <TableRowText
            leadingComponent={
              <Project
                size={iconSizeOptions['ICON-L']}
                stroke={
                  isActive
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }
              />
            }
            mainText={
              <RowText
                as="button"
                type="button"
                onClick={() => onOpenProject(row.original.id)}
                weight={textStyleWeightOptions['TEXT-600']}
                size={textStyleSizeOptions['TEXT-R']}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  textAlign: 'left',
                  color: isActive
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }}
              >
                {row.original.name}
              </RowText>
            }
            secondaryText={
              <RowTextContainer>
                {!isActive && (
                  <RowText
                    weight={textStyleWeightOptions['TEXT-500']}
                    size={textStyleSizeOptions['TEXT-XS']}
                    color={colorsOptions.greys['grey-40']}
                  >
                    {inactiveLabel}
                  </RowText>
                )}
              </RowTextContainer>
            }
          />
          {!isActive && (
            <BadgesComponent
              variant={badgesVariantOptions.HIGHLIGHTED}
              color={badgeColorOptions.DISABLED}
              size={badgeSizeOptions.SMALL}
            >
              {inactiveLabel}
            </BadgesComponent>
          )}
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true,
      minWidth: 240
    }
  },
  {
    id: myProjectSortOptions.COMPANY,
    accessorKey: 'organisationName',
    header: formatMessage({
      id: 'company',
      defaultMessage: 'Company'
    }),
    size: isDesktop ? tableColumnSizeOptions.L : tableColumnSizeOptions.M,
    cell: ({ row }) =>
      renderTextCell({
        isActive: row.original.active,
        value: row.original.organisationName
      }),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.ROLE,
    accessorKey: 'role',
    header: formatMessage({
      id: 'role',
      defaultMessage: 'Role'
    }),
    size: isDesktop ? tableColumnSizeOptions.M : tableColumnSizeOptions.XS,
    cell: ({ row }) => {
      const roleType = row.original.role?.type
      const roleName = row.original.role?.name
      const roleLabel = roleType
        ? formatMessage({
            id: `personas_roleLabel${roleType}`,
            defaultMessage: roleName ?? roleType
          })
        : (roleName ?? '')

      return (
        <TableRowCell>
          {roleLabel ? (
            <BadgesComponent
              variant={badgesVariantOptions.STRONG}
              size={badgeSizeOptions.SMALL}
              color={handleRoleColor(roleType)}
            >
              {roleLabel}
            </BadgesComponent>
          ) : null}
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.LAST_MODIFIED,
    accessorKey: 'lastModified',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    cell: ({ row }) =>
      renderTextCell({
        isActive: row.original.active,
        value: row.original.lastModifiedLabel
      }),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.OPEN_PINS,
    accessorKey: 'openPins',
    header: formatMessage({
      id: 'open_pins',
      defaultMessage: 'Open pins'
    }),
    size: tableColumnSizeOptions.M,
    cell: ({ row }) =>
      renderTextCell({
        isActive: row.original.active,
        value: row.original.openPins
      }),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.OPEN_TASKS,
    accessorKey: 'openTasks',
    header: formatMessage({
      id: 'open_tasks',
      defaultMessage: 'Open tasks'
    }),
    size: tableColumnSizeOptions.M,
    cell: ({ row }) =>
      renderTextCell({
        isActive: row.original.active,
        value: row.original.openTasks
      }),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'projectSettings',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => {
      if (row.original.role?.name !== ROLES.POWERUSER) {
        return null
      }

      return (
        <TableRowCell>
          <IconTooltip
            description={formatMessage({
              id: 'settings'
            })}
          >
            <ButtonComponent
              variant={buttonVariantOptions.GHOST}
              buttonType={buttonTypeOptions['ICON-ONLY']}
              onClick={() => onOpenProjectSettings(row.original.id)}
            >
              <Settings
                style={{
                  fill: row.original.active
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40'],
                  strokeWidth: 0
                }}
              />
            </ButtonComponent>
          </IconTooltip>
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    accessorKey: 'projectPanel',
    header: null,
    size: tableColumnSizeOptions.XS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell>
        <IconTooltip
          description={formatMessage({
            id: 'project_info'
          })}
        >
          <ButtonComponent
            variant={buttonVariantOptions.GHOST}
            buttonType={buttonTypeOptions['ICON-ONLY']}
            onClick={() => onOpenProjectPanel(row.original.id)}
          >
            <Info
              stroke={
                row.original.active
                  ? colorsOptions.greys['grey-90']
                  : colorsOptions.greys['grey-40']
              }
            />
          </ButtonComponent>
        </IconTooltip>
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    accessorKey: 'contextMenu',
    header: null,
    size: tableColumnSizeOptions.XS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell>
        <MyProjectsTableContextMenu
          item={row.original}
          onCopyProject={onCopyProject}
          onLeaveProject={onLeaveProject}
        />
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.RIGHT
    }
  }
]

export const buildColumnsSkeleton = ({
  formatMessage = projectMenuFormatMessageFallback
} = {}) => [
  {
    id: myProjectSortOptions.NAME,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    enableSorting: false,
    cell: renderTitleCellSkeleton,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true
    }
  },
  {
    id: myProjectSortOptions.COMPANY,
    accessorKey: 'organisationName',
    header: formatMessage({
      id: 'company',
      defaultMessage: 'Company'
    }),
    size: tableColumnSizeOptions.L,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.ROLE,
    accessorKey: 'role',
    header: formatMessage({
      id: 'role',
      defaultMessage: 'Role'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: myProjectSortOptions.LAST_MODIFIED,
    accessorKey: 'lastModified',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: 'open-pins',
    accessorKey: 'openPins',
    header: formatMessage({
      id: 'open_pins',
      defaultMessage: 'Open pins'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: 'open-tasks',
    accessorKey: 'openTasks',
    header: formatMessage({
      id: 'open_tasks',
      defaultMessage: 'Open tasks'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'projectSettings',
    header: null,
    size: tableColumnSizeOptions.XS,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'projectPanel',
    header: null,
    size: tableColumnSizeOptions.XS,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'contextMenu',
    header: null,
    size: tableColumnSizeOptions.XS,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  }
]

export const columnsDefault = buildColumnsDefault()
export const columnsSkeleton = buildColumnsSkeleton()
